import React from 'react';
import { Button } from '../ui/button';
import { AirlineLogo } from '../../utils/airlineLogos';
import { Money } from '../../contexts/CurrencyContext';

// Local airport time: take HH:MM straight from the string (never via Date → browser TZ).
export const hhmm = (t) => {
  if (!t) return '--:--';
  const s = String(t);
  const m = s.match(/T(\d{2}):(\d{2})/) || s.match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : s;
};

export const shortDate = (d, withWeekday = false) => {
  if (!d) return '';
  const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString('en-GB', withWeekday ? { weekday: 'short', day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short' });
};

export const prettyDuration = (d) => (d ? String(d).replace('PT', '').replace('H', 'h ').replace('M', 'm').trim() : '');

export const cabinLabel = (code) => {
  const c = String(code || 'Y').toUpperCase();
  return { Y: 'Economy', M: 'Economy', ECONOMY: 'Economy', W: 'Premium Economy', S: 'Premium Economy', 'PREMIUM-ECONOMY': 'Premium Economy', 'PREMIUM ECONOMY': 'Premium Economy', C: 'Business', J: 'Business', BUSINESS: 'Business', F: 'First', FIRST: 'First' }[c] || code || 'Economy';
};

export const baggageLabel = (f) => {
  const n = f?.baggage_count || 0;
  if (f?.baggage_included === false || n === 0) return 'Hand luggage only';
  const w = parseInt(f?.baggage_weight) || parseInt(f?.hold_baggage_weight) || 23;
  return n > 1 ? `${n} × ${w} kg` : `${w} kg hold bag`;
};

const nextDay = (leg) => leg.arrival_date && leg.departure_date && String(leg.arrival_date).slice(0, 10) !== String(leg.departure_date).slice(0, 10);

export const LegTimeline = ({ leg, compact = false }) => {
  const stops = leg.stops || 0;
  const layovers = leg.layovers || [];
  const timeCls = compact ? 'text-base sm:text-lg' : 'text-xl sm:text-2xl';
  return (
    <div className="flex items-center gap-2 sm:gap-4" data-testid="leg-timeline">
      <div className="min-w-[56px] sm:min-w-[72px]">
        <div className={`${timeCls} font-bold text-[#222222] leading-none`} title={`Local time at ${leg.from}`}>{hhmm(leg.departure_time)}</div>
        <div className="mt-1 text-sm font-semibold text-slate-700">{leg.from}</div>
        <div className="text-[11px] text-slate-500">{shortDate(leg.departure_date)}</div>
      </div>
      <div className="flex-1 px-1 sm:px-3">
        <div className="text-center text-[11px] sm:text-xs font-medium text-slate-500 mb-1">{prettyDuration(leg.duration)}</div>
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full border-2 border-[#222222] bg-white" />
          <div className="flex-1 h-[2px] bg-slate-300 relative">
            {stops > 0 && (
              <div className="absolute inset-0 flex items-center justify-evenly">
                {(layovers.length ? layovers : Array.from({ length: stops })).map((_, i) => <div key={i} className="w-2.5 h-2.5 rounded-full bg-[#FF6600] ring-2 ring-white" />)}
              </div>
            )}
          </div>
          <div className="w-2 h-2 rounded-full bg-[#222222]" />
        </div>
        <div className="text-center mt-1 text-[10px] sm:text-[11px] font-medium leading-tight">
          {stops === 0
            ? <span className="text-emerald-600">Direct</span>
            : layovers.length
              ? layovers.map((l, i) => <div key={i} className="text-[#FF6600]">{l.duration_display || l.duration} in {l.airport}</div>)
              : <span className="text-[#FF6600]">{stops} stop{stops > 1 ? 's' : ''}</span>}
        </div>
      </div>
      <div className="min-w-[56px] sm:min-w-[72px] text-right">
        <div className={`${timeCls} font-bold text-[#222222] leading-none`} title={`Local time at ${leg.to}`}>
          {hhmm(leg.arrival_time)}{nextDay(leg) && <sup className="text-[10px] sm:text-xs text-[#FF6600] font-bold ml-0.5">+1</sup>}
        </div>
        <div className="mt-1 text-sm font-semibold text-slate-700">{leg.to}</div>
        <div className="text-[11px] text-slate-500">{shortDate(leg.arrival_date)}</div>
      </div>
    </div>
  );
};

const LegDivider = ({ label }) => (
  <div className="my-3 flex items-center gap-2">
    <div className="flex-1 h-px bg-slate-200" />
    <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{label}</span>
    <div className="flex-1 h-px bg-slate-200" />
  </div>
);

const BADGE_STYLES = {
  orange: 'bg-[#FF6600] text-white',
  dark: 'bg-[#222222] text-white',
  green: 'bg-emerald-600 text-white',
  outline: 'bg-white text-[#222222] border border-slate-300',
  soft: 'bg-orange-50 text-[#FF6600] border border-orange-200',
};

// Modern flight card: white surface, airline strip, timeline(s), price rail with the orange CTA.
// `price` is always a number already in GBP; the caller decides per-person vs total via `priceCaption`.
export const BfmFlightCard = ({
  leg, returnLeg, itin, price, priceCaption = 'per person', priceNote, badges = [], title, subtitle, secondaryAirlineCode,
  onSelect, selectLabel = 'Select', disabled, selected, highlight, testId, children, footer,
}) => {
  const stops = leg.stops || 0;
  const multiAirline = secondaryAirlineCode && secondaryAirlineCode !== leg.airline_code;
  return (
    <div
      className={`group relative overflow-hidden bg-white rounded-2xl border transition-[box-shadow,border-color,transform] duration-200 hover:shadow-[0_12px_32px_-12px_rgba(34,34,34,0.25)] hover:-translate-y-px ${selected || highlight ? 'border-[#FF6600] shadow-[0_0_0_2px_rgba(255,102,0,0.18)]' : 'border-slate-200 hover:border-[#FF6600]/70'}`}
      data-testid={testId}
    >
      {highlight && <div className="absolute inset-x-0 top-0 h-1 bg-[#FF6600]" />}
      <div className="px-3 sm:px-5 pt-3 sm:pt-4 flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-1.5 shrink-0">
            <AirlineLogo code={leg.airline_code} className="w-10 h-10" />
            {multiAirline && <><span className="text-slate-400 font-bold">+</span><AirlineLogo code={secondaryAirlineCode} className="w-10 h-10" /></>}
          </div>
          <div className="min-w-0 leading-tight">
            <div className="font-bold text-[#222222] text-sm sm:text-base truncate" data-testid={testId ? `${testId}-airline` : undefined}>{title || leg.airline_name || leg.airline || leg.airline_code}</div>
            <div className="text-xs text-slate-500 truncate">{subtitle || `${leg.flight_number || ''}${leg.segments?.length > 1 ? ` +${leg.segments.length - 1} connecting` : ''}`}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0">
          {badges.filter(Boolean).map((b, i) => <span key={i} className={`text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full ${BADGE_STYLES[b.tone] || b.cls || BADGE_STYLES.soft}`} data-testid={b.testId}>{b.text}</span>)}
          <span className={`text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full ${stops === 0 && (!returnLeg || !returnLeg.stops) ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
            {stops === 0 && (!returnLeg || !returnLeg.stops) ? 'Direct' : returnLeg ? `${stops} / ${returnLeg.stops || 0} stops` : `${stops} stop${stops > 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      <div className="px-3 sm:px-5 py-3 sm:py-4 flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-6">
        <div className="flex-1 min-w-0">
          {returnLeg && <LegDivider label={`Outbound · ${shortDate(leg.departure_date, true)}`} />}
          <LegTimeline leg={leg} compact={!!returnLeg} />
          {returnLeg && (
            <>
              <LegDivider label={`Return · ${shortDate(returnLeg.departure_date, true)}`} />
              <LegTimeline leg={returnLeg} compact />
            </>
          )}
          {children}
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] sm:text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md">{cabinLabel(leg.cabin_code || itin?.cabin_class || itin?.travel_class)}</span>
            <span className="text-[10px] sm:text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md">{baggageLabel(itin)}</span>
            {itin?.is_ndc && <span className="text-[10px] sm:text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md">NDC fare</span>}
          </div>
        </div>

        <div className="lg:w-44 lg:border-l lg:border-slate-100 lg:pl-6 flex lg:flex-col items-center lg:items-end justify-between lg:justify-center gap-2 pt-3 lg:pt-0 border-t lg:border-t-0 border-slate-100">
          <div className="text-left lg:text-right">
            <div className="text-xl sm:text-2xl font-black text-[#FF6600] leading-none" data-testid={testId ? `${testId}-price` : undefined}><Money gbp={Math.round(price).toLocaleString()} /></div>
            <div className="text-[10px] sm:text-xs text-slate-500 mt-1">{priceCaption}</div>
            {priceNote && <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5">{priceNote}</div>}
          </div>
          {onSelect && (
            <Button onClick={onSelect} disabled={disabled} className="bg-[#FF6600] hover:bg-[#E65C00] active:bg-[#CC5200] text-white font-bold px-5 h-10 rounded-full text-sm shadow-sm lg:w-full lg:mt-2" data-testid={testId ? `${testId}-select` : undefined}>
              {selectLabel}
            </Button>
          )}
        </div>
      </div>
      {footer && <div className="px-3 sm:px-5 pb-3 sm:pb-4">{footer}</div>}
    </div>
  );
};
